import { useState, useEffect, useRef } from "react"
import { flushSync } from "react-dom"
import HeaderSection from "../components/header-section"
import Sidebar from "../features/sidebar/components/sidebar"
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "../components/ui/card"
import { Tabs, TabsList, TabsTrigger, TabsContent } from "../components/ui/tabs"
import { Button } from "../components/ui/button"
import { Input } from "../components/ui/input"
import { Label } from "../components/ui/label"
import { Textarea } from "../components/ui/textarea"
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../components/ui/select"
import {
  Brain,
  Sparkles,
  Settings,
  Key,
  AlertCircle,
  CheckCircle,
  Plus,
  ChevronDown,
  ChevronUp,
  Pencil,
  Check,
  Clock,
} from "lucide-react"
import {
  useCreateSimulationMutation,
  useUpdateSimulationMutation,
  useGenerateSimulationMutation,
  usePollGenerationStatusQuery,
  useGetTemplatesForStepQuery,
  useGetActiveTemplateQuery,
  useCreatePromptTemplateMutation,
  useSetActiveTemplateMutation,
  useExportSimulationMutation,
  useListSimulationsQuery,
  useGetSimulationQuery,
  useGetAiProvidersQuery,
  type SimulationScenario,
  type GenerationOptions,
  type PromptTemplate,
  type SimulationListItem,
  type UpdateSimulationRequest,
} from "../app/services/simulationCreatorApi"
import { PromptEditor } from "../components/PromptEditor"
import { ActionsTreeDisplay } from "../components/ActionsTreeDisplay"
import GenerationHistory from "../components/GenerationHistory"
import { MeasureMappingStep } from "../components/MeasureMappingStep"
import { type ActionCategory } from "../app/services/simulationCreatorApi"


// Scenario Input Form Component
const ScenarioForm = ({
  scenario,
  setScenario,
}: {
  scenario: SimulationScenario
  setScenario: (scenario: SimulationScenario) => void
}) => {
  return (
    <Card>
      <CardHeader>
        <CardTitle className="flex items-center gap-2">
          <Brain className="h-5 w-5" />
          Simulation Scenario
        </CardTitle>
        <CardDescription>
          Define the business context and participant details
        </CardDescription>
      </CardHeader>
      <CardContent className="space-y-4">
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div className="space-y-2">
            <Label htmlFor="industry">Industry</Label>
            <Input
              id="industry"
              placeholder="e.g., Healthcare Technology"
              value={scenario.industry || ""}
              onChange={(e) =>
                setScenario({ ...scenario, industry: e.target.value })
              }
            />
          </div>
          <div className="space-y-2">
            <Label htmlFor="role">Participant Role</Label>
            <Input
              id="role"
              placeholder="e.g., Chief Technology Officer"
              value={scenario.participantRole || ""}
              onChange={(e) =>
                setScenario({ ...scenario, participantRole: e.target.value })
              }
            />
          </div>
        </div>

        <div className="space-y-2">
          <Label htmlFor="company">Company Description</Label>
          <Textarea
            id="company"
            placeholder="Describe the company, its size, products/services..."
            value={scenario.companyDescription || ""}
            onChange={(e) =>
              setScenario({ ...scenario, companyDescription: e.target.value })
            }
            rows={3}
          />
        </div>

        <div className="space-y-2">
          <Label htmlFor="challenges">Company Challenges</Label>
          <Textarea
            id="challenges"
            placeholder="What challenges is the company facing?"
            value={scenario.companyChallenges || ""}
            onChange={(e) =>
              setScenario({ ...scenario, companyChallenges: e.target.value })
            }
            rows={3}
          />
        </div>

        <div className="space-y-2">
          <Label htmlFor="objectives">Participant Objectives</Label>
          <Textarea
            id="objectives"
            placeholder="What should the participant accomplish in this simulation?"
            value={scenario.participantObjectives || ""}
            onChange={(e) =>
              setScenario({
                ...scenario,
                participantObjectives: e.target.value,
              })
            }
            rows={3}
          />
        </div>
      </CardContent>
    </Card>
  )
}

// Generation Steps
type GenerationStep =
  | "scenario"
  | "scenario-details"
  | "stakeholders"
  | "messages"
  | "actions"
  | "information-search"
  | "measure-mapping"
  | "complete"

type ScenarioDetails = {
  company_name?: string
  industry?: string
  challenge_summary?: string
  crisis_description?: string
  key_stakeholders?: string[]
}

type Stakeholder = {
  name: string
  position: string
  description: string
}

type StakeholdersResponse = {
  stakeholders: Stakeholder[]
}

type GeneratedMessage = {
  text: string
  validateResponse: string
}

type MessagesResponse = {
  messages: GeneratedMessage[]
}

type Action = {
  text: string
  categories: string[]
}

type ActionsResponse = {
  actions: Action[]
}

type InformationResponse = {
  response: string
  time: string
}

type InformationTopic = {
  text: string
  responses: InformationResponse[]
}

type InformationSubcategory = {
  text: string
  categories: InformationTopic[]
}

type InformationCategory = {
  text: string
  categories: InformationSubcategory[]
}

type InformationSearchResponse = {
  categories: InformationCategory[]
}

type StepData = {
  scenarioDetails?: ScenarioDetails | string
  stakeholders?: StakeholdersResponse | string
  messages?: MessagesResponse | string
  actions?: ActionsResponse | string
  informationSearch?: InformationSearchResponse | string
}

const parseTotalMinutes = (mmss: string): number => {
  const [min, sec] = mmss.split(":").map(Number)
  return (min || 0) + ((sec || 0) > 0 ? 1 : 0)
}

// Main Simulation Creator Component
const SimulationCreator = () => {
  const [apiKeys, setApiKeys] = useState<Record<string, string>>({})
  const [scenario, setScenario] = useState<SimulationScenario>({
    industry: "",
    companyDescription: "",
    companyChallenges: "",
    participantRole: "",
    participantObjectives: "",
  })
  const [settings, setSettings] = useState<GenerationOptions>({
    aiProvider: "openai",
    aiModel: "gpt-5-nano", // Most cost-efficient model in GPT-5 series
    temperature: 0.7,
    maxTokens: undefined,
  })
  const [currentStep, setCurrentStep] = useState<GenerationStep>("scenario")
  const [currentSimulationId, setCurrentSimulationId] = useState<string | null>(
    null,
  )
  const [currentGenerationId, setCurrentGenerationId] = useState<string | null>(
    null,
  )
  const [simulationName, setSimulationName] = useState("")
  const [isEditingName, setIsEditingName] = useState(false)
  const [stepData, setStepData] = useState<StepData>({})
  const [selectedSimulation, setSelectedSimulation] = useState<SimulationListItem | null>(null)
  const [settingsPanelOpen, setSettingsPanelOpen] = useState(false)
  const [isSubmittingForApproval, setIsSubmittingForApproval] = useState(false)
  const [submissionSuccess, setSubmissionSuccess] = useState(false)
  const [timingSettings, setTimingSettings] = useState({
    totalLength: "30:00",
    emergencyStartTime: "17:00",
    emergencyEndTime: "27:00",
  })

  // API hooks
  const [createSimulation] = useCreateSimulationMutation()
  const [updateSimulation] = useUpdateSimulationMutation()
  const [generateSimulation] = useGenerateSimulationMutation()
  const [exportSimulation, { isLoading: isExporting }] = useExportSimulationMutation()
  const { data: simulationsList } = useListSimulationsQuery({ page: 1, limit: 100 })
  const { data: loadedSimulation } = useGetSimulationQuery(currentSimulationId!, { skip: !currentSimulationId })
  const { data: aiProviders } = useGetAiProvidersQuery()

  const handleExport = async () => {
    if (!currentSimulationId) return
    
    try {
      const result = await exportSimulation({
        simulationId: currentSimulationId,
        format: 'JSON'
      }).unwrap()
      
      // Create blob and download
      const url = window.URL.createObjectURL(result)
      const a = document.createElement('a')
      a.href = url
      a.download = `${simulationName || 'simulation'}.json`
      document.body.appendChild(a)
      a.click()
      window.URL.revokeObjectURL(url)
      document.body.removeChild(a)
    } catch (error) {
      console.error('Export failed:', error)
    }
  }

  const handleSubmitForApproval = async () => {
    if (!currentSimulationId) return

    setIsSubmittingForApproval(true)
    try {
      await updateSimulation({
        id: currentSimulationId,
        updates: {
          status: 'COMPLETED',
          settings: {
            estimatedDuration: parseTotalMinutes(timingSettings.totalLength),
            totalLength: timingSettings.totalLength,
            emergencyStartTime: timingSettings.emergencyStartTime,
            emergencyEndTime: timingSettings.emergencyEndTime,
            difficulty: 'INTERMEDIATE',
          },
        } as Partial<UpdateSimulationRequest>,
      }).unwrap()
      setSubmissionSuccess(true)
    } catch (error) {
      console.error('Failed to submit for approval:', error)
    } finally {
      setIsSubmittingForApproval(false)
    }
  }

  const handleSimulationSelect = (simulation: SimulationListItem) => {
    setSelectedSimulation(simulation)
    setCurrentSimulationId(simulation.id)
    setSimulationName("")
    setScenario({
      industry: "",
      companyDescription: "",
      companyChallenges: "",
      participantRole: "",
      participantObjectives: "",
    })
    setStepData({})
    setCurrentStep("scenario")
    setUserSelectedStep(null) // Reset user selection tracking when loading a new simulation
    setSubmissionSuccess(false)
  }

  const handleCreateNew = () => {
    setSelectedSimulation(null)
    setCurrentSimulationId(null)
    setSimulationName("")
    setStepData({})
    setCurrentStep("scenario")
    setUserSelectedStep(null) // Reset user selection tracking
  }
  
  // Prompt template hooks
  const [createPromptTemplate] = useCreatePromptTemplateMutation()
  const [setActiveTemplate] = useSetActiveTemplateMutation()
  
  // Helper to map UI step names to API step names
  const getApiStepName = (uiStep: string): string => {
    const stepMap: Record<string, string> = {
      'scenario-details': 'SCENARIO_DETAILS',
      'stakeholders': 'STAKEHOLDERS', 
      'messages': 'MESSAGES',
      'actions': 'ACTIONS',
      'information-search': 'INFORMATION'
    }
    return stepMap[uiStep] || uiStep.toUpperCase()
  }
  
  // Get templates for current step
  const currentApiStep = getApiStepName(currentStep)
  const { data: templatesData } = useGetTemplatesForStepQuery(currentApiStep)
  const { data: activeTemplateData } = useGetActiveTemplateQuery(currentApiStep)
  
  const templates = templatesData?.templates || []
  const activeTemplate = activeTemplateData?.template || undefined

  // State to track if we should stop polling
  const [shouldStopPolling, setShouldStopPolling] = useState(false)
  
  // State to track if we're clearing data (for immediate UI feedback)
  const [isClearing, setIsClearing] = useState(false)

  // Poll generation status when we have a generation ID
  const { data: generationStatus } = usePollGenerationStatusQuery(
    {
      simulationId: currentSimulationId!,
      generationId: currentGenerationId!,
    },
    {
      skip: !currentSimulationId || !currentGenerationId,
      pollingInterval: shouldStopPolling ? 0 : 2000, // Stop polling by setting interval to 0
    },
  )

  // Stop polling when generation is completed or failed
  useEffect(() => {
    if (generationStatus?.status === "COMPLETED" || generationStatus?.status === "FAILED") {
      console.log(`Generation ${generationStatus.status.toLowerCase()}, stopping polling`)
      setShouldStopPolling(true)
    }
  }, [generationStatus?.status])

  // Reset polling state when starting new generation
  useEffect(() => {
    if (currentGenerationId) {
      console.log(`Starting polling for generation: ${currentGenerationId}`)
      setShouldStopPolling(false)
    }
  }, [currentGenerationId])

  // Load API keys and settings from localStorage on mount
  useEffect(() => {
    const storedKeys = localStorage.getItem("sim-creator-api-keys")
    if (storedKeys) {
      try {
        setApiKeys(JSON.parse(storedKeys))
      } catch (e) {
        console.error("Failed to parse stored API keys:", e)
      }
    }

    const storedSettings = localStorage.getItem("sim-creator-settings")
    if (storedSettings) {
      try {
        const parsed = JSON.parse(storedSettings)
        setSettings(prev => ({ ...prev, ...parsed }))
      } catch (e) {
        console.error("Failed to parse stored settings:", e)
      }
    }
  }, [])

  // Track if user has manually selected a step to prevent auto-jumping
  const [userSelectedStep, setUserSelectedStep] = useState<string | null>(null)

  // Determine the maximum step the user can access based on their progress
  const getMaxAllowedStep = (): GenerationStep => {
    // Always allow scenario step
    if (!stepData.scenarioDetails) return "scenario-details"
    if (!stepData.stakeholders) return "stakeholders"
    if (!stepData.messages) return "messages"
    if (!stepData.actions) return "actions"
    if (!stepData.informationSearch) return "information-search"
    // Measure mapping is always accessible after information search (classification is optional)
    return "complete"
  }

  // Check if a step is accessible to the user
  const isStepAccessible = (step: GenerationStep): boolean => {
    const steps: GenerationStep[] = [
      "scenario",
      "scenario-details",
      "stakeholders",
      "messages",
      "actions",
      "information-search",
      "measure-mapping",
      "complete"
    ]
    
    const maxAllowedStep = getMaxAllowedStep()
    const currentStepIndex = steps.indexOf(step)
    const maxStepIndex = steps.indexOf(maxAllowedStep)
    
    return currentStepIndex <= maxStepIndex
  }

  // Clear dependent steps when regenerating an earlier step
  const clearDependentSteps = (regeneratingStep: string) => {
    console.log('clearDependentSteps called for:', regeneratingStep)
    const stepHierarchy = [
      "scenario-details",
      "stakeholders",
      "messages",
      "actions",
      "information-search"
    ]

    const regeneratingIndex = stepHierarchy.indexOf(regeneratingStep)
    console.log('Regenerating index:', regeneratingIndex)
    if (regeneratingIndex === -1) return

    // Clear the current step being regenerated AND all steps that come after it
    const newStepData = { ...stepData }
    console.log('Current stepData:', stepData)

    // Clear the step being regenerated
    switch (regeneratingStep) {
      case "scenario-details":
        newStepData.scenarioDetails = undefined
        break
      case "stakeholders":
        newStepData.stakeholders = undefined
        break
      case "messages":
        newStepData.messages = undefined
        break
      case "actions":
        newStepData.actions = undefined
        break
      case "information-search":
        newStepData.informationSearch = undefined
        break
    }

    // Clear all steps that come after the regenerating step
    for (let i = regeneratingIndex + 1; i < stepHierarchy.length; i++) {
      const stepToReset = stepHierarchy[i]
      switch (stepToReset) {
        case "stakeholders":
          newStepData.stakeholders = undefined
          break
        case "messages":
          newStepData.messages = undefined
          break
        case "actions":
          newStepData.actions = undefined
          break
        case "information-search":
          newStepData.informationSearch = undefined
          break
      }
    }
    
    console.log('New stepData after clearing:', newStepData)
    setStepData(newStepData)
    console.log('setStepData called with cleared data')
    
    // If the user is currently on a step that was cleared, move them back
    const currentStepIndex = stepHierarchy.indexOf(currentStep)
    if (currentStepIndex > regeneratingIndex) {
      setCurrentStep(regeneratingStep as GenerationStep)
      setUserSelectedStep(regeneratingStep)
    }
  }

  // Load simulation data when simulation is selected
  useEffect(() => {
    if (loadedSimulation) {
      const sim = loadedSimulation
      console.log('Loading simulation:', sim)
      setSimulationName(sim.name)

      // Load scenario data from the simulation
      const loadedScenario = {
        industry: sim.scenario?.industry || sim.industry || '',
        companyDescription: sim.scenario?.companyDescription || sim.companyDescription || '',
        companyChallenges: sim.scenario?.companyChallenges || sim.companyChallenges || '',
        participantRole: sim.scenario?.participantRole || sim.participantRole || '',
        participantObjectives: sim.scenario?.participantObjectives || sim.participantObjectives || '',
      }
      console.log('Setting scenario from loaded simulation:', loadedScenario)
      setScenario(loadedScenario)

      // Pre-populate timing settings if the simulation has them
      if (sim.settings) {
        setTimingSettings({
          totalLength: sim.settings.totalLength || "30:00",
          emergencyStartTime: sim.settings.emergencyStartTime || "17:00",
          emergencyEndTime: sim.settings.emergencyEndTime || "27:00",
        })
      }

      // Don't repopulate step data if a generation is actively in progress
      // (clearDependentSteps already cleared it and we don't want to undo that)
      if (!isActivelyGeneratingRef.current) {
        // Load generated content if available
        const newStepData: StepData = {}
        if (sim.questionAnswers) newStepData.scenarioDetails = sim.questionAnswers as ScenarioDetails | string
        if (sim.stakeholders) newStepData.stakeholders = sim.stakeholders as StakeholdersResponse | string
        if (sim.messages) newStepData.messages = sim.messages as MessagesResponse | string
        if (sim.actionTree) newStepData.actions = sim.actionTree as ActionsResponse | string
        if (sim.informationTree) newStepData.informationSearch = sim.informationTree as InformationSearchResponse

        setStepData(newStepData)
      }

      // Only auto-determine step if user hasn't manually selected one
      if (!userSelectedStep) {
        // Determine the current step based on what's completed
        if (sim.informationTree) {
          setCurrentStep("complete")
        } else if (sim.actionTree) {
          setCurrentStep("information-search")
        } else if (sim.messages) {
          setCurrentStep("actions")
        } else if (sim.stakeholders) {
          setCurrentStep("messages")
        } else if (sim.questionAnswers) {
          setCurrentStep("stakeholders")
        } else {
          setCurrentStep("scenario-details")
        }
      }
    }
  }, [loadedSimulation, userSelectedStep])

  // Validate that the current step is still accessible when stepData changes
  useEffect(() => {
    if (userSelectedStep && !isStepAccessible(currentStep)) {
      // If the current step is no longer accessible, move to max allowed
      setCurrentStep(getMaxAllowedStep())
      setUserSelectedStep(null)
    }
  }, [stepData])

  const canGenerate = () => {
    const hasRequiredFields =
      scenario.industry &&
      scenario.companyDescription &&
      scenario.companyChallenges &&
      scenario.participantRole &&
      scenario.participantObjectives

    // If we've already generated scenario details, we can proceed even without all basic info fields
    // This fixes the issue where subsequent generation steps are blocked after scenario generation
    const hasScenarioContent = Boolean(stepData.scenarioDetails)

    const hasApiKey = settings.aiProvider && apiKeys[settings.aiProvider]
    const hasModel = settings.aiModel

    // Only check generation status if it matches the current generation ID
    // If there's no current generation ID, we're not generating
    const isCurrentlyGenerating = currentGenerationId && generationStatus &&
      generationStatus.generationId === currentGenerationId &&
      (generationStatus.status === "PENDING" || generationStatus.status === "IN_PROGRESS")

    const notGenerating = !isCurrentlyGenerating

    // Debug logging for troubleshooting
    if (!(hasRequiredFields || hasScenarioContent) || !hasApiKey || !hasModel || !notGenerating) {
      console.log('canGenerate failed:', {
        hasRequiredFields,
        hasScenarioContent,
        hasApiKey: Boolean(hasApiKey),
        aiProvider: settings.aiProvider,
        hasModel,
        aiModel: settings.aiModel,
        notGenerating,
        isCurrentlyGenerating,
        currentGenerationId,
        generationStatus: generationStatus?.status,
        generationId: generationStatus?.generationId
      })
    }

    return (hasRequiredFields || hasScenarioContent) && hasApiKey && hasModel && notGenerating
  }

  const getCannotGenerateReason = (): string => {
    const hasRequiredFields =
      scenario.industry &&
      scenario.companyDescription &&
      scenario.companyChallenges &&
      scenario.participantRole &&
      scenario.participantObjectives
    const hasScenarioContent = Boolean(stepData.scenarioDetails)
    if (!(hasRequiredFields || hasScenarioContent)) {
      return "Please fill in all scenario fields first"
    }
    if (!settings.aiProvider || !apiKeys[settings.aiProvider]) {
      return "Please configure AI provider and API key in the sidebar"
    }
    if (!settings.aiModel) {
      return "Please select an AI model in the sidebar"
    }
    return "A generation is already in progress"
  }

  const handleGenerateStep = async (step: string) => {
    console.log('Starting generation for step:', step)
    console.log('Current simulation ID:', currentSimulationId)
    console.log('Current generation ID:', currentGenerationId)
    
    // FIRST: Clear data immediately and synchronously
    console.log('Clearing step data IMMEDIATELY before any async operations:', step)
    console.log('StepData before clearing:', stepData)
    
    // Use flushSync to force immediate React re-render after state update
    flushSync(() => {
      setIsClearing(true)
      clearDependentSteps(step)
    })
    console.log('Data cleared and UI forcibly updated immediately')
    
    // THEN: Check if we can generate (after clearing data)
    const canGen = canGenerate()
    console.log('Can generate?', canGen)
    console.log('Settings:', settings)
    console.log('API Keys available:', Object.keys(apiKeys))
    
    if (!canGen) {
      console.log('Cannot generate - requirements not met. Check the debug log above for details.')
      setIsClearing(false) // Reset clearing state on error
      return
    }
    
    // Reset polling state to ensure fresh start (but don't clear generation ID until we have a new one)
    setShouldStopPolling(false)
    setIsClearing(false) // Clear the "clearing" state since we're now starting generation

    try {
      // Create simulation if we don't have one
      let simulationId = currentSimulationId
      if (!simulationId) {
        console.log('Creating new simulation...')
        const autoName = simulationName || `${scenario.participantRole} - ${scenario.industry}`.slice(0, 255)
        const newSim = await createSimulation({
          name: autoName,
          description: `${scenario.participantRole} simulation in ${scenario.industry}`,
          industry: scenario.industry,
          // Note: Additional scenario details are stored when generation completes
          // The full scenario object is passed in each generation request
        }).unwrap()
        simulationId = newSim.id
        setCurrentSimulationId(simulationId)
        setSimulationName(autoName)
        setSelectedSimulation(newSim)
        console.log('Created simulation with ID:', simulationId)

        // Save scenario data to the new simulation
        await updateSimulation({
          id: simulationId,
          updates: {
            scenario: scenario,
          }
        }).unwrap()
      }

      // Map step to generation type
      const stepTypeMap: Record<string, string> = {
        "scenario-details": "SCENARIO_ONLY",
        stakeholders: "STAKEHOLDERS_ONLY",
        messages: "MESSAGES_ONLY",
        actions: "ACTIONS_ONLY",
        "information-search": "INFORMATION_ONLY",
      }

      console.log('Generating with type:', stepTypeMap[step])
      console.log('Scenario data being sent:', scenario)
      console.log('Active template being used:', activeTemplate)

      // Generate this step's content
      const generationResponse = await generateSimulation({
        simulationId,
        request: {
          type: stepTypeMap[step] as
            | "SCENARIO_ONLY"
            | "STAKEHOLDERS_ONLY"
            | "MESSAGES_ONLY"
            | "ACTIONS_ONLY"
            | "INFORMATION_ONLY",
          scenario,
          templateId: activeTemplate?.id, // Use the user's selected template
          options: {
            ...settings,
            apiKey: settings.aiProvider
              ? apiKeys[settings.aiProvider]
              : undefined,
          },
        },
      }).unwrap()

      console.log('Generation response:', generationResponse)
      setCurrentGenerationId(generationResponse.generationId)
    } catch (error) {
      console.error("Generation failed:", error)
      setIsClearing(false) // Reset clearing state on error
    }
  }

  const saveScenarioToSimulation = async () => {
    if (!currentSimulationId) return
    
    try {
      await updateSimulation({
        id: currentSimulationId,
        updates: {
          scenario: scenario,
          industry: scenario.industry,
          description: `${scenario.participantRole} simulation in ${scenario.industry}`,
        }
      }).unwrap()
      console.log('Scenario saved successfully')
    } catch (error) {
      console.error('Failed to save scenario:', error)
    }
  }

  const handleNextStep = async () => {
    const steps: GenerationStep[] = [
      "scenario",
      "scenario-details",
      "stakeholders",
      "messages",
      "actions",
      "information-search",
      "measure-mapping",
      "complete",
    ]
    const currentIndex = steps.indexOf(currentStep)
    
    // Save scenario changes when leaving the scenario step
    if (currentStep === "scenario" && currentSimulationId) {
      await saveScenarioToSimulation()
    }
    
    if (currentIndex < steps.length - 1) {
      setCurrentStep(steps[currentIndex + 1])
      setCurrentGenerationId(null) // Reset generation state for new step
    }
  }

  const handlePreviousStep = () => {
    const steps: GenerationStep[] = [
      "scenario",
      "scenario-details",
      "stakeholders",
      "messages",
      "actions",
      "information-search",
      "measure-mapping",
      "complete",
    ]
    const currentIndex = steps.indexOf(currentStep)
    if (currentIndex > 0) {
      setCurrentStep(steps[currentIndex - 1])
      setCurrentGenerationId(null) // Reset generation state
    }
  }

  // Prompt template handlers
  const handleSavePromptTemplate = async (template: Partial<PromptTemplate>) => {
    try {
      await createPromptTemplate({
        name: template.name || `${currentStep} Template`,
        description: template.description,
        step: getApiStepName(currentStep),
        systemPrompt: template.systemPrompt || '',
        userPrompt: template.userPrompt || '',
        variables: template.variables,
        parentId: template.parentId
      }).unwrap()
    } catch (error) {
      console.error("Failed to save prompt template:", error)
    }
  }

  const handleSelectPromptTemplate = async (templateId: string) => {
    try {
      await setActiveTemplate(templateId).unwrap()
    } catch (error) {
      console.error("Failed to set active template:", error)
    }
  }

  // Only consider generation status if it matches the current generation ID
  const isCurrentGeneration = currentGenerationId && generationStatus && 
    generationStatus.generationId === currentGenerationId
    
  const isGenerating = Boolean(isCurrentGeneration && (
    generationStatus?.status === "PENDING" ||
    generationStatus?.status === "IN_PROGRESS"
  ))
  const generationFailed = Boolean(isCurrentGeneration && generationStatus?.status === "FAILED")
  const generationCompleted = Boolean(isCurrentGeneration && generationStatus?.status === "COMPLETED")

  // True from the moment user clicks Generate until generation completes/fails.
  // Covers the gap before the first poll returns (where isGenerating is briefly false).
  const isActivelyGenerating = isGenerating || Boolean(
    currentGenerationId && !generationCompleted && !generationFailed
  )

  // Ref to expose isActivelyGenerating to the loadedSimulation effect without adding it as a dependency
  const isActivelyGeneratingRef = useRef(false)
  useEffect(() => {
    isActivelyGeneratingRef.current = isActivelyGenerating
  }, [isActivelyGenerating])

  // Update step data when generation completes
  useEffect(() => {
    if (generationCompleted && generationStatus?.content) {
      console.log('Generation completed for step:', currentStep)
      console.log('Generated content:', generationStatus.content)
      
      // Map the step to the correct data key
      const stepDataKeyMap: Record<string, string> = {
        'scenario-details': 'scenarioDetails',
        'information-search': 'informationSearch',
      }
      const stepDataKey = stepDataKeyMap[currentStep] || currentStep
      
      setStepData((prev) => {
        const newData = {
          ...prev,
          [stepDataKey]: generationStatus.content,
        }
        console.log('Updated stepData with key:', stepDataKey)
        console.log('New step data:', newData)
        return newData
      })
    }
  }, [generationCompleted, generationStatus?.content, currentStep])

  const renderCurrentStep = () => {
    switch (currentStep) {
      case "scenario":
        return (
          <TabsContent value="scenario">
            <Card>
            <CardHeader>
              <CardTitle>Step 1: Define Your Scenario</CardTitle>
              <CardDescription>
                Provide the basic information about your simulation scenario. Review and edit as needed before proceeding.
              </CardDescription>
            </CardHeader>
            <CardContent>
              <div className="space-y-6">
                <ScenarioForm scenario={scenario} setScenario={setScenario} />
                
                {/* Review Section */}
                {scenario.industry && scenario.companyDescription && (
                  <div className="border-t pt-6">
                    <h4 className="font-medium mb-4">📋 Review Your Scenario:</h4>
                    <div className="bg-gray-50 p-4 rounded-lg space-y-3 text-sm">
                      <div><strong>Industry:</strong> {scenario.industry}</div>
                      <div><strong>Role:</strong> {scenario.participantRole}</div>
                      <div><strong>Company:</strong> {scenario.companyDescription}</div>
                      <div><strong>Challenges:</strong> {scenario.companyChallenges}</div>
                      <div><strong>Objectives:</strong> {scenario.participantObjectives}</div>
                    </div>
                  </div>
                )}
                
                <div className="flex justify-between">
                  <div></div>
                  <Button
                    onClick={handleNextStep}
                    disabled={!scenario.industry || !scenario.companyDescription || !scenario.companyChallenges || !scenario.participantRole || !scenario.participantObjectives}>
                    Next: Generate Scenario Details
                  </Button>
                </div>
              </div>
            </CardContent>
          </Card>
          </TabsContent>
        )

      case "scenario-details":
        return (
          <TabsContent value="scenario-details">
            <div className="max-w-4xl mx-auto space-y-6">
              <PromptEditor
                step={currentApiStep}
                stepName="Scenario Details"
                currentTemplate={activeTemplate}
                onSave={handleSavePromptTemplate}
                onVersionSelect={handleSelectPromptTemplate}
                templates={templates}
              />
              
              <GenerationHistory
                simulationId={currentSimulationId!}
                currentStep="SCENARIO_ONLY"
                onRegenerateClick={() => handleGenerateStep("scenario-details")}
                collapsible={true}
                defaultCollapsed={true}
              />
              {!stepData.scenarioDetails && (
                <div className="text-center py-8">
                  <p className="text-muted-foreground mb-4">
                    Ready to generate detailed scenario information
                  </p>
                  <Button
                    onClick={() => handleGenerateStep("scenario-details")}
                    disabled={!canGenerate() || isGenerating}>
                    {isGenerating ? "Generating..." : "Generate Scenario Details"}
                  </Button>
                  {!canGenerate() && !isActivelyGenerating && (
                    <p className="text-xs text-red-500 mt-2">
                      {getCannotGenerateReason()}
                    </p>
                  )}
                </div>
              )}

              {stepData.scenarioDetails && !isActivelyGenerating && (
                <div className="space-y-4">
                  <h4 className="font-medium">🎯 Generated Scenario Details:</h4>
                  <div className="bg-blue-50 border border-blue-200 p-4 rounded-lg">
                    {(() => {
                      try {
                        const details = typeof stepData.scenarioDetails === 'string' 
                          ? JSON.parse(stepData.scenarioDetails) as ScenarioDetails
                          : stepData.scenarioDetails as ScenarioDetails
                        
                        return (
                          <div className="space-y-3 text-sm">
                            {details.company_name && <div><strong>Company Name:</strong> {details.company_name}</div>}
                            {details.industry && <div><strong>Industry:</strong> {details.industry}</div>}
                            {details.challenge_summary && <div><strong>Challenge Summary:</strong> {details.challenge_summary}</div>}
                            {details.crisis_description && <div><strong>Crisis Description:</strong> {details.crisis_description}</div>}
                            {details.key_stakeholders && details.key_stakeholders.length > 0 && (
                              <div>
                                <strong>Key Stakeholders:</strong>
                                <ul className="list-disc list-inside ml-2 mt-1">
                                  {details.key_stakeholders.map((stakeholder, idx) => (
                                    <li key={idx}>{stakeholder}</li>
                                  ))}
                                </ul>
                              </div>
                            )}
                          </div>
                        )
                      } catch (e) {
                        return (
                          <div>
                            <p className="text-amber-600 mb-2">Raw output (parsing failed):</p>
                            <pre className="bg-gray-100 p-2 rounded text-xs overflow-auto">
                              {JSON.stringify(stepData.scenarioDetails, null, 2)}
                            </pre>
                          </div>
                        )
                      }
                    })()}
                  </div>
                  <div className="flex gap-2">
                    <Button
                      variant="outline"
                      onClick={() => handleGenerateStep("scenario-details")}
                      disabled={isClearing || isGenerating}>
                      {isClearing ? "Clearing..." : "🔄 Regenerate"}
                    </Button>
                  </div>
                  
                  {/* Expandable JSON Editor */}
                  <div className="border-t pt-4">
                    <details className="group">
                      <summary className="cursor-pointer flex items-center gap-2 text-sm font-medium text-gray-600 hover:text-gray-800">
                        <span className="transform transition-transform group-open:rotate-90">▶</span>
                        View/Edit Raw JSON
                      </summary>
                      <div className="mt-3 space-y-2">
                        <textarea
                          className="w-full h-40 p-3 border rounded font-mono text-sm"
                          value={JSON.stringify(stepData.scenarioDetails, null, 2)}
                          onChange={(e) => {
                            try {
                              const parsed = JSON.parse(e.target.value)
                              setStepData(prev => ({ ...prev, scenarioDetails: parsed }))
                            } catch (err) {
                              // Invalid JSON, but still update the raw value
                              setStepData(prev => ({ ...prev, scenarioDetails: e.target.value }))
                            }
                          }}
                        />
                        <p className="text-xs text-gray-500">
                          Edit the JSON above. Valid JSON will be parsed automatically.
                        </p>
                      </div>
                    </details>
                  </div>
                </div>
              )}

              <div className="flex justify-between">
                <Button variant="outline" onClick={handlePreviousStep}>
                  Previous
                </Button>
                <Button
                  onClick={handleNextStep}
                  disabled={!stepData.scenarioDetails}>
                  Next: Generate Stakeholders
                </Button>
              </div>
            </div>
          </TabsContent>
        )

      case "stakeholders":
        return (
          <TabsContent value="stakeholders">
            <div className="max-w-4xl mx-auto space-y-6">
              <PromptEditor
                step={currentApiStep}
                stepName="Stakeholders"
                currentTemplate={activeTemplate}
                onSave={handleSavePromptTemplate}
                onVersionSelect={handleSelectPromptTemplate}
                templates={templates}
              />
              
              <GenerationHistory
                simulationId={currentSimulationId!}
                currentStep="STAKEHOLDERS_ONLY"
                onRegenerateClick={() => handleGenerateStep("stakeholders")}
                collapsible={true}
                defaultCollapsed={true}
              />
              {!stepData.stakeholders && (
                <div className="text-center py-8">
                  <p className="text-muted-foreground mb-4">
                    Ready to generate stakeholders
                  </p>
                  <Button 
                    onClick={() => handleGenerateStep("stakeholders")} 
                    disabled={!canGenerate() || isGenerating}>
                    {isGenerating ? "Generating..." : "Generate Stakeholders"}
                  </Button>
                  {!canGenerate() && !isActivelyGenerating && (
                    <p className="text-xs text-red-500 mt-2">
                      {getCannotGenerateReason()}
                    </p>
                  )}
                </div>
              )}

              {stepData.stakeholders && !isActivelyGenerating && (
                <div className="space-y-4">
                  <h4 className="font-medium">👥 Generated Stakeholders:</h4>
                  <div className="bg-green-50 border border-green-200 p-4 rounded-lg">
                    {(() => {
                      try {
                        const stakeholders = typeof stepData.stakeholders === 'string' 
                          ? JSON.parse(stepData.stakeholders) as StakeholdersResponse
                          : stepData.stakeholders as StakeholdersResponse
                        
                        if (stakeholders.stakeholders && Array.isArray(stakeholders.stakeholders)) {
                          return (
                            <div className="grid gap-4">
                              {stakeholders.stakeholders.map((stakeholder, idx) => (
                                <div key={idx} className="bg-white p-3 rounded border">
                                  <h5 className="font-medium text-green-800">{stakeholder.name}</h5>
                                  <p className="text-sm text-green-600 mb-2">{stakeholder.position}</p>
                                  <p className="text-sm">{stakeholder.description}</p>
                                </div>
                              ))}
                            </div>
                          )
                        }
                        
                        return (
                          <pre className="bg-gray-100 p-2 rounded text-xs overflow-auto">
                            {JSON.stringify(stakeholders, null, 2)}
                          </pre>
                        )
                      } catch (e) {
                        return (
                          <div>
                            <p className="text-amber-600 mb-2">Raw output (parsing failed):</p>
                            <pre className="bg-gray-100 p-2 rounded text-xs overflow-auto">
                              {JSON.stringify(stepData.stakeholders, null, 2)}
                            </pre>
                          </div>
                        )
                      }
                    })()}
                  </div>
                  <div className="flex gap-2">
                    <Button
                      variant="outline"
                      onClick={() => handleGenerateStep("stakeholders")}
                      disabled={isClearing || isGenerating}>
                      {isClearing ? "Clearing..." : "🔄 Regenerate"}
                    </Button>
                  </div>
                  
                  {/* Expandable JSON Editor */}
                  <div className="border-t pt-4">
                    <details className="group">
                      <summary className="cursor-pointer flex items-center gap-2 text-sm font-medium text-gray-600 hover:text-gray-800">
                        <span className="transform transition-transform group-open:rotate-90">▶</span>
                        View/Edit Raw JSON
                      </summary>
                      <div className="mt-3 space-y-2">
                        <textarea
                          className="w-full h-40 p-3 border rounded font-mono text-sm"
                          value={JSON.stringify(stepData.stakeholders, null, 2)}
                          onChange={(e) => {
                            try {
                              const parsed = JSON.parse(e.target.value)
                              setStepData(prev => ({ ...prev, stakeholders: parsed }))
                            } catch (err) {
                              setStepData(prev => ({ ...prev, stakeholders: e.target.value }))
                            }
                          }}
                        />
                        <p className="text-xs text-gray-500">
                          Edit the JSON above. Valid JSON will be parsed automatically.
                        </p>
                      </div>
                    </details>
                  </div>
                </div>
              )}

              <div className="flex justify-between">
                <Button variant="outline" onClick={handlePreviousStep}>
                  Previous
                </Button>
                <Button
                  onClick={handleNextStep}
                  disabled={!stepData.stakeholders}>
                  Next: Generate Messages
                </Button>
              </div>
            </div>
          </TabsContent>
        )

      case "messages":
        return (
          <TabsContent value="messages">
            <div className="max-w-4xl mx-auto space-y-6">
              <PromptEditor
                step={currentApiStep}
                stepName="Messages"
                currentTemplate={activeTemplate}
                onSave={handleSavePromptTemplate}
                onVersionSelect={handleSelectPromptTemplate}
                templates={templates}
              />
              
              <GenerationHistory
                simulationId={currentSimulationId!}
                currentStep="MESSAGES_ONLY"
                onRegenerateClick={() => handleGenerateStep("messages")}
                collapsible={true}
                defaultCollapsed={true}
              />
              {!stepData.messages && (
                <div className="text-center py-8">
                  <p className="text-muted-foreground mb-4">
                    {isClearing ? "Clearing previous data..." : "Ready to generate messages"}
                  </p>
                  <Button 
                    onClick={() => handleGenerateStep("messages")} 
                    disabled={!canGenerate() || isGenerating || isClearing}>
                    {isClearing ? "Clearing..." : isGenerating ? "Generating..." : "Generate Messages"}
                  </Button>
                  {!canGenerate() && !isGenerating && !isClearing && (
                    <p className="text-xs text-red-500 mt-2">
                      {getCannotGenerateReason()}
                    </p>
                  )}
                </div>
              )}

              {stepData.messages && !isActivelyGenerating && (
                <div className="space-y-4">
                  <h4 className="font-medium">📬 Generated Messages:</h4>
                  <div className="bg-purple-50 border border-purple-200 p-4 rounded-lg">
                    {(() => {
                      try {
                        const messages = typeof stepData.messages === 'string' 
                          ? JSON.parse(stepData.messages) as MessagesResponse
                          : stepData.messages as MessagesResponse
                        
                        return (
                          <div className="space-y-3">
                            {messages.messages.map((message, idx) => {
                              const text = typeof message === 'string' ? message : message.text
                              const validateResponse = typeof message === 'string' ? null : message.validateResponse
                              return (
                                <div key={idx} className="bg-white p-3 rounded border border-l-4 border-l-purple-400">
                                  <div className="flex items-start gap-3">
                                    <div className="flex-shrink-0 w-8 h-8 bg-purple-100 rounded-full flex items-center justify-center text-purple-600 font-medium text-sm">
                                      {idx + 1}
                                    </div>
                                    <div className="flex-1">
                                      <p className="text-sm text-gray-700 leading-relaxed">{text}</p>
                                      {validateResponse && (
                                        <p className="text-xs text-purple-700 mt-2 italic">
                                          <span className="font-medium not-italic">Source: </span>
                                          {validateResponse}
                                        </p>
                                      )}
                                    </div>
                                  </div>
                                </div>
                              )
                            })}
                          </div>
                        )
                      } catch (e) {
                        return (
                          <div>
                            <p className="text-amber-600 mb-2">Raw output (parsing failed):</p>
                            <pre className="bg-gray-100 p-2 rounded text-xs overflow-auto">
                              {JSON.stringify(stepData.messages, null, 2)}
                            </pre>
                          </div>
                        )
                      }
                    })()}
                  </div>
                  <div className="flex gap-2">
                    <Button
                      variant="outline"
                      onClick={() => handleGenerateStep("messages")}
                      disabled={isClearing || isGenerating}>
                      {isClearing ? "Clearing..." : "🔄 Regenerate"}
                    </Button>
                  </div>
                  
                  {/* Expandable JSON Editor */}
                  <div className="border-t pt-4">
                    <details className="group">
                      <summary className="cursor-pointer flex items-center gap-2 text-sm font-medium text-gray-600 hover:text-gray-800">
                        <span className="transform transition-transform group-open:rotate-90">▶</span>
                        View/Edit Raw JSON
                      </summary>
                      <div className="mt-3 space-y-2">
                        <textarea
                          className="w-full h-40 p-3 border rounded font-mono text-sm"
                          value={JSON.stringify(stepData.messages, null, 2)}
                          onChange={(e) => {
                            try {
                              const parsed = JSON.parse(e.target.value)
                              setStepData(prev => ({ ...prev, messages: parsed }))
                            } catch (err) {
                              setStepData(prev => ({ ...prev, messages: e.target.value }))
                            }
                          }}
                        />
                        <p className="text-xs text-gray-500">
                          Edit the JSON above. Valid JSON will be parsed automatically.
                        </p>
                      </div>
                    </details>
                  </div>
                </div>
              )}

              <div className="flex justify-between">
                <Button variant="outline" onClick={handlePreviousStep}>
                  Previous
                </Button>
                <Button
                  onClick={handleNextStep}
                  disabled={!stepData.messages}>
                  Next: Generate Actions
                </Button>
              </div>
            </div>
          </TabsContent>
        )

      case "actions":
        return (
          <TabsContent value="actions">
            <div className="max-w-4xl mx-auto space-y-6">
              <PromptEditor
                step={currentApiStep}
                stepName="Actions"
                currentTemplate={activeTemplate}
                onSave={handleSavePromptTemplate}
                onVersionSelect={handleSelectPromptTemplate}
                templates={templates}
              />
              
              <GenerationHistory
                simulationId={currentSimulationId!}
                currentStep="ACTIONS_ONLY"
                onRegenerateClick={() => handleGenerateStep("actions")}
                collapsible={true}
                defaultCollapsed={true}
              />
              {!stepData.actions && (
                <div className="text-center py-8">
                  <p className="text-muted-foreground mb-4">
                    Ready to generate actions
                  </p>
                  <Button 
                    onClick={() => handleGenerateStep("actions")} 
                    disabled={!canGenerate() || isGenerating}>
                    {isGenerating ? "Generating..." : "Generate Actions"}
                  </Button>
                  {!canGenerate() && !isActivelyGenerating && (
                    <p className="text-xs text-red-500 mt-2">
                      {getCannotGenerateReason()}
                    </p>
                  )}
                </div>
              )}

              {stepData.actions && !isActivelyGenerating && (
                <div className="space-y-4">
                  {/* Tree Display */}
                  <ActionsTreeDisplay
                    actions={(() => {
                      try {
                        const actions = typeof stepData.actions === 'string' 
                          ? JSON.parse(stepData.actions) 
                          : stepData.actions
                        
                        // Handle both the old flat format and new nested format
                        if (Array.isArray(actions)) {
                          return actions // New nested format
                        } else if (actions.actions && Array.isArray(actions.actions)) {
                          // Old flat format - convert to nested format for display
                          return actions.actions.map((action: { text: string; categories?: string[] }) => ({
                            text: action.text,
                            categories: action.categories ? action.categories.map((cat: string) => ({ text: cat })) : []
                          }))
                        }
                        
                        return []
                      } catch (e) {
                        return []
                      }
                    })()}
                    title="Generated Actions"
                    description="Explore the decision tree for this simulation"
                  />
                  
                  <div className="flex gap-2">
                    <Button
                      variant="outline"
                      onClick={() => handleGenerateStep("actions")}
                      disabled={isClearing || isGenerating}>
                      {isClearing ? "Clearing..." : "🔄 Regenerate"}
                    </Button>
                  </div>
                  
                  {/* Expandable JSON Editor */}
                  <div className="border-t pt-4">
                    <details className="group">
                      <summary className="cursor-pointer flex items-center gap-2 text-sm font-medium text-gray-600 hover:text-gray-800">
                        <span className="transform transition-transform group-open:rotate-90">▶</span>
                        View/Edit Raw JSON
                      </summary>
                      <div className="mt-3 space-y-2">
                        <textarea
                          className="w-full h-40 p-3 border rounded font-mono text-sm"
                          value={JSON.stringify(stepData.actions, null, 2)}
                          onChange={(e) => {
                            try {
                              const parsed = JSON.parse(e.target.value)
                              setStepData(prev => ({ ...prev, actions: parsed }))
                            } catch (err) {
                              setStepData(prev => ({ ...prev, actions: e.target.value }))
                            }
                          }}
                        />
                        <p className="text-xs text-gray-500">
                          Edit the JSON above. Valid JSON will be parsed automatically.
                        </p>
                      </div>
                    </details>
                  </div>
                </div>
              )}

              <div className="flex justify-between">
                <Button variant="outline" onClick={handlePreviousStep}>
                  Previous
                </Button>
                <Button onClick={handleNextStep} disabled={!stepData.actions}>
                  Next: Information Search
                </Button>
              </div>
            </div>
          </TabsContent>
        )

      case "information-search":
        return (
          <TabsContent value="information-search">
            <div className="max-w-4xl mx-auto space-y-6">
              <PromptEditor
                step={currentApiStep}
                stepName="Information Search"
                currentTemplate={activeTemplate}
                onSave={handleSavePromptTemplate}
                onVersionSelect={handleSelectPromptTemplate}
                templates={templates}
              />

              <GenerationHistory
                simulationId={currentSimulationId!}
                currentStep="INFORMATION_ONLY"
                onRegenerateClick={() => handleGenerateStep("information-search")}
                collapsible={true}
                defaultCollapsed={true}
              />
              {!stepData.informationSearch && (
                <div className="text-center py-8">
                  <p className="text-muted-foreground mb-4">
                    Ready to generate information search content
                  </p>
                  <Button
                    onClick={() => handleGenerateStep("information-search")}
                    disabled={!canGenerate() || isGenerating}>
                    {isGenerating ? "Generating..." : "Generate Information Search"}
                  </Button>
                  {!canGenerate() && !isActivelyGenerating && (
                    <p className="text-xs text-red-500 mt-2">
                      {getCannotGenerateReason()}
                    </p>
                  )}
                </div>
              )}

              {stepData.informationSearch && !isActivelyGenerating && (
                <div className="space-y-4">
                  <h4 className="font-medium">Generated Information Search:</h4>
                  <div className="bg-teal-50 border border-teal-200 p-4 rounded-lg">
                    {(() => {
                      try {
                        const data = typeof stepData.informationSearch === 'string'
                          ? JSON.parse(stepData.informationSearch)
                          : stepData.informationSearch
                        const categories = data?.categories || (Array.isArray(data) ? data : [])
                        return (
                          <div className="space-y-3">
                            <p className="text-sm text-teal-700">
                              {categories.length} top-level categories generated
                            </p>
                            {categories.map((cat: InformationCategory, i: number) => (
                              <details key={i} className="group">
                                <summary className="cursor-pointer font-medium text-sm text-teal-800 hover:text-teal-600">
                                  {cat.text || `Category ${i + 1}`}
                                  <span className="text-xs text-teal-500 ml-2">
                                    ({cat.categories?.length || 0} subcategories)
                                  </span>
                                </summary>
                                <div className="ml-4 mt-2 space-y-2">
                                  {(cat.categories || []).map((sub: InformationSubcategory, j: number) => (
                                    <details key={j} className="group">
                                      <summary className="cursor-pointer text-sm text-teal-700 hover:text-teal-500">
                                        {sub.text || `Subcategory ${j + 1}`}
                                        <span className="text-xs text-teal-400 ml-2">
                                          ({sub.categories?.length || 0} topics)
                                        </span>
                                      </summary>
                                      <div className="ml-4 mt-1 space-y-1">
                                        {(sub.categories || []).map((topic: InformationTopic, k: number) => (
                                          <div key={k} className="text-xs text-teal-600 border-l-2 border-teal-200 pl-2">
                                            <span className="font-medium">{topic.text}</span>
                                            {topic.responses?.length > 0 && (
                                              <p className="text-teal-500 mt-0.5 italic">
                                                &ldquo;{topic.responses[0].response?.substring(0, 120)}...&rdquo;
                                              </p>
                                            )}
                                          </div>
                                        ))}
                                      </div>
                                    </details>
                                  ))}
                                </div>
                              </details>
                            ))}
                          </div>
                        )
                      } catch {
                        return <pre className="text-xs overflow-auto">{JSON.stringify(stepData.informationSearch, null, 2)}</pre>
                      }
                    })()}
                  </div>

                  <div className="flex gap-2">
                    <Button
                      variant="outline"
                      onClick={() => handleGenerateStep("information-search")}
                      disabled={isClearing || isGenerating}>
                      {isClearing ? "Clearing..." : "Regenerate"}
                    </Button>
                  </div>

                  {/* Expandable JSON Editor */}
                  <div className="border-t pt-4">
                    <details className="group">
                      <summary className="cursor-pointer flex items-center gap-2 text-sm font-medium text-gray-600 hover:text-gray-800">
                        <span className="transform transition-transform group-open:rotate-90">&#9654;</span>
                        View/Edit Raw JSON
                      </summary>
                      <div className="mt-3 space-y-2">
                        <textarea
                          className="w-full h-40 p-3 border rounded font-mono text-sm"
                          value={JSON.stringify(stepData.informationSearch, null, 2)}
                          onChange={(e) => {
                            try {
                              const parsed = JSON.parse(e.target.value)
                              setStepData(prev => ({ ...prev, informationSearch: parsed }))
                            } catch (err) {
                              setStepData(prev => ({ ...prev, informationSearch: e.target.value }))
                            }
                          }}
                        />
                        <p className="text-xs text-gray-500">
                          Edit the JSON above. Valid JSON will be parsed automatically.
                        </p>
                      </div>
                    </details>
                  </div>
                </div>
              )}

              <div className="flex justify-between">
                <Button variant="outline" onClick={handlePreviousStep}>
                  Previous
                </Button>
                <Button onClick={handleNextStep} disabled={!stepData.informationSearch}>
                  Next: Measure Mapping
                </Button>
              </div>
            </div>
          </TabsContent>
        )

      case "measure-mapping": {
        // Parse actions for the measure mapping step
        const parseActionsForMeasures = (data: ActionsResponse | string | undefined): ActionCategory[] => {
          if (!data) return []
          if (typeof data === "string") {
            try {
              const parsed = JSON.parse(data)
              return Array.isArray(parsed) ? parsed : (parsed.actions || [])
            } catch {
              return []
            }
          }
          return Array.isArray(data) ? data as unknown as ActionCategory[] : (data.actions as unknown as ActionCategory[] || [])
        }

        const actionsForMeasures = parseActionsForMeasures(stepData.actions)

        return (
          <MeasureMappingStep
            actions={actionsForMeasures}
            apiKeys={apiKeys}
            settings={settings}
            simulationId={currentSimulationId || ""}
            onActionsUpdate={(updatedActions) => {
              setStepData(prev => ({
                ...prev,
                actions: { actions: updatedActions } as unknown as ActionsResponse
              }))
            }}
            onPrevious={handlePreviousStep}
            onNext={handleNextStep}
          />
        )
      }

      case "complete":
        return (
          <TabsContent value="complete">
            <Card>
              <CardHeader>
                <CardTitle>
                  {submissionSuccess ? 'Submitted for Approval!' : 'Simulation Complete!'}
                </CardTitle>
                <CardDescription>
                  {submissionSuccess
                    ? 'Your simulation has been submitted and is awaiting admin approval before it can be assigned to organizations.'
                    : 'Your simulation has been generated successfully. Submit it for approval to make it available for organizations.'}
                </CardDescription>
              </CardHeader>
              <CardContent>
                {submissionSuccess ? (
                  <div className="space-y-4">
                    <div className="bg-green-50 border border-green-200 rounded-lg p-4">
                      <div className="flex items-center gap-2 text-green-800">
                        <CheckCircle className="h-5 w-5" />
                        <span className="font-medium">Submission Successful</span>
                      </div>
                      <p className="text-sm text-green-700 mt-2">
                        Your simulation is now in the pending approval queue. An admin will review and approve it shortly.
                      </p>
                    </div>
                    <div className="flex justify-center gap-2">
                      <Button
                        variant="outline"
                        onClick={() => {
                          setCurrentStep("scenario")
                          setSubmissionSuccess(false)
                          setCurrentSimulationId(null)
                          setSelectedSimulation(null)
                        }}>
                        Create Another Simulation
                      </Button>
                      <Button onClick={handleExport} disabled={isExporting}>
                        {isExporting ? 'Exporting...' : 'Export Simulation'}
                      </Button>
                    </div>
                  </div>
                ) : (
                  <>
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-6">
                      <div>
                        <h4 className="font-medium mb-2">Generated Components:</h4>
                        <ul className="space-y-1 text-sm text-muted-foreground">
                          <li className="flex items-center gap-2"><CheckCircle className="h-4 w-4 text-green-600" /> Scenario Details</li>
                          <li className="flex items-center gap-2"><CheckCircle className="h-4 w-4 text-green-600" /> Stakeholders</li>
                          <li className="flex items-center gap-2"><CheckCircle className="h-4 w-4 text-green-600" /> Messages</li>
                          <li className="flex items-center gap-2"><CheckCircle className="h-4 w-4 text-green-600" /> Actions</li>
                          <li className="flex items-center gap-2"><CheckCircle className="h-4 w-4 text-green-600" /> Information Search</li>
                        </ul>
                      </div>
                      <div>
                        <h4 className="font-medium mb-2">What Happens Next:</h4>
                        <ul className="space-y-1 text-sm text-muted-foreground">
                          <li>1. Submit simulation for admin approval</li>
                          <li>2. Admin reviews and approves</li>
                          <li>3. Simulation becomes available to assign to organizations</li>
                        </ul>
                      </div>
                    </div>

                    <div className="border rounded-lg p-4 mb-6">
                      <h4 className="font-medium mb-3 flex items-center gap-2">
                        <Clock className="h-4 w-4" />
                        Simulation Timing
                      </h4>
                      <p className="text-sm text-muted-foreground mb-4">
                        Configure the duration and emergency window for the simulation. Times are in MM:SS format.
                      </p>
                      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                        <div>
                          <Label htmlFor="totalLength">Total Length</Label>
                          <Input
                            id="totalLength"
                            placeholder="MM:SS"
                            value={timingSettings.totalLength}
                            onChange={(e) => setTimingSettings(prev => ({ ...prev, totalLength: e.target.value }))}
                          />
                          <p className="text-xs text-muted-foreground mt-1">How long the simulation runs</p>
                        </div>
                        <div>
                          <Label htmlFor="emergencyStartTime">Emergency Start</Label>
                          <Input
                            id="emergencyStartTime"
                            placeholder="MM:SS"
                            value={timingSettings.emergencyStartTime}
                            onChange={(e) => setTimingSettings(prev => ({ ...prev, emergencyStartTime: e.target.value }))}
                          />
                          <p className="text-xs text-muted-foreground mt-1">When the crisis period begins</p>
                        </div>
                        <div>
                          <Label htmlFor="emergencyEndTime">Emergency End</Label>
                          <Input
                            id="emergencyEndTime"
                            placeholder="MM:SS"
                            value={timingSettings.emergencyEndTime}
                            onChange={(e) => setTimingSettings(prev => ({ ...prev, emergencyEndTime: e.target.value }))}
                          />
                          <p className="text-xs text-muted-foreground mt-1">When the crisis period ends</p>
                        </div>
                      </div>
                    </div>

                    <div className="flex justify-between">
                      <Button variant="outline" onClick={handlePreviousStep}>
                        Previous
                      </Button>
                      <div className="flex gap-2">
                        <Button
                          variant="outline"
                          onClick={() => setCurrentStep("scenario")}>
                          Create Another
                        </Button>
                        <Button variant="outline" onClick={handleExport} disabled={isExporting}>
                          {isExporting ? 'Exporting...' : 'Export'}
                        </Button>
                        <Button
                          onClick={handleSubmitForApproval}
                          disabled={isSubmittingForApproval}
                          className="bg-green-600 hover:bg-green-700"
                        >
                          {isSubmittingForApproval ? 'Submitting...' : 'Submit for Approval'}
                        </Button>
                      </div>
                    </div>
                  </>
                )}
              </CardContent>
            </Card>
          </TabsContent>
        )

      default:
        return null
    }
  }



  return (
    <div className="relative bg-base-white w-full h-[100dvh] flex flex-row items-start justify-start font-text-md-regular">
      <Sidebar />
      <div className="w-full h-[100dvh] flex-1 flex flex-col">
        <HeaderSection />
        <div className="flex-1 overflow-auto bg-gray-50">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-6 space-y-6">
            {/* Header with Page Title and Settings Button */}
            <div className="flex items-center justify-between">
              <div>
                <h1 className="text-3xl font-bold flex items-center gap-2">
                  <Sparkles className="h-8 w-8 text-primary" />
                  Simulation Creator
                </h1>
                <div className="mt-1 flex items-center gap-1">
                  {isEditingName ? (
                    <form
                      className="flex items-center gap-1"
                      onSubmit={async (e) => {
                        e.preventDefault()
                        setIsEditingName(false)
                        if (currentSimulationId && simulationName.trim()) {
                          try {
                            await updateSimulation({
                              id: currentSimulationId,
                              updates: { name: simulationName.trim() }
                            }).unwrap()
                          } catch (err) {
                            console.error('Failed to update simulation name:', err)
                          }
                        }
                      }}
                    >
                      <Input
                        autoFocus
                        className="h-7 w-64 text-sm"
                        value={simulationName}
                        onChange={(e: React.ChangeEvent<HTMLInputElement>) => setSimulationName(e.target.value)}
                        onBlur={async () => {
                          setIsEditingName(false)
                          if (currentSimulationId && simulationName.trim()) {
                            try {
                              await updateSimulation({
                                id: currentSimulationId,
                                updates: { name: simulationName.trim() }
                              }).unwrap()
                            } catch (err) {
                              console.error('Failed to update simulation name:', err)
                            }
                          }
                        }}
                        placeholder="Simulation name..."
                      />
                      <Button type="submit" variant="ghost" size="sm" className="h-7 w-7 p-0">
                        <Check className="h-3.5 w-3.5" />
                      </Button>
                    </form>
                  ) : (
                    <button
                      type="button"
                      className="text-muted-foreground text-sm flex items-center gap-1 hover:text-foreground transition-colors"
                      onClick={() => setIsEditingName(true)}
                    >
                      {selectedSimulation?.name || simulationName || "New Simulation"}
                      <Pencil className="h-3 w-3" />
                    </button>
                  )}
                  <span className="text-muted-foreground text-sm">
                    {settings.aiProvider && ` | ${settings.aiProvider}`}
                    {settings.aiModel && ` - ${settings.aiModel}`}
                  </span>
                </div>
              </div>
              <div className="flex items-center gap-2">
                <Select
                  value={selectedSimulation?.id || "new"}
                  onValueChange={(value) => {
                    if (value === "new") {
                      handleCreateNew()
                    } else {
                      const simulation = simulationsList?.data?.find(s => s.id === value)
                      if (simulation) {
                        handleSimulationSelect(simulation)
                      }
                    }
                  }}
                >
                  <SelectTrigger className="w-[200px] h-9 bg-white border-2 border-gray-400 text-gray-800 hover:bg-gray-100 hover:border-gray-500 transition-colors shadow-sm">
                    <SelectValue placeholder="Select simulation..." />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="new">
                      <div className="flex items-center">
                        <Plus className="mr-2 h-4 w-4" />
                        New Simulation
                      </div>
                    </SelectItem>
                    {simulationsList?.data && simulationsList.data.length > 0 && (
                      <>
                        {simulationsList.data.map((simulation) => (
                          <SelectItem key={simulation.id} value={simulation.id}>
                            {simulation.name}
                          </SelectItem>
                        ))}
                      </>
                    )}
                  </SelectContent>
                </Select>
                <div className="relative">
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => setSettingsPanelOpen(!settingsPanelOpen)}
                    className="flex items-center gap-2 bg-white border-2 border-gray-400 text-gray-800 hover:bg-gray-100 hover:border-gray-500 transition-colors shadow-sm"
                  >
                    <Settings className="h-4 w-4" />
                    Settings
                    {settingsPanelOpen ? (
                      <ChevronUp className="h-4 w-4" />
                    ) : (
                      <ChevronDown className="h-4 w-4" />
                    )}
                  </Button>
                {settingsPanelOpen && (
                  <Card className="absolute right-0 top-full mt-2 z-50 w-[450px] shadow-lg">
                    <CardContent className="p-4">
                      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    {/* AI Provider & Model */}
                    <div className="space-y-4">
                      <div className="space-y-2">
                        <Label className="text-sm font-medium">AI Provider</Label>
                        <Select
                          value={settings.aiProvider || ""}
                          onValueChange={(value) =>
                            setSettings({ ...settings, aiProvider: value, aiModel: "" })
                          }
                        >
                          <SelectTrigger>
                            <SelectValue placeholder="Select provider..." />
                          </SelectTrigger>
                          <SelectContent>
                            {/* Re-use providers from query */}
                            <SelectItem value="openai">OpenAI {!apiKeys["openai"] && "(API key needed)"}</SelectItem>
                            <SelectItem value="anthropic">Anthropic {!apiKeys["anthropic"] && "(API key needed)"}</SelectItem>
                            <SelectItem value="google">Google {!apiKeys["google"] && "(API key needed)"}</SelectItem>
                          </SelectContent>
                        </Select>
                      </div>
                      <div className="space-y-2">
                        <Label className="text-sm font-medium">Model</Label>
                        <Select
                          value={settings.aiModel || ""}
                          onValueChange={(value) => {
                            if (value === "__custom__") {
                              setSettings({ ...settings, aiModel: "" })
                            } else {
                              setSettings({ ...settings, aiModel: value })
                            }
                          }}
                        >
                          <SelectTrigger>
                            <SelectValue placeholder="Select model..." />
                          </SelectTrigger>
                          <SelectContent>
                            {(aiProviders?.providers
                              ?.find(p => p.id === settings.aiProvider)
                              ?.models || []
                            ).map((modelId) => (
                              <SelectItem key={modelId} value={modelId}>
                                {modelId}
                              </SelectItem>
                            ))}
                            <SelectItem value="__custom__">Other (enter manually)</SelectItem>
                          </SelectContent>
                        </Select>
                        {settings.aiModel !== undefined && !(aiProviders?.providers
                          ?.find(p => p.id === settings.aiProvider)
                          ?.models || []).includes(settings.aiModel) && (
                          <Input
                            value={settings.aiModel}
                            onChange={(e) => setSettings({ ...settings, aiModel: e.target.value })}
                            placeholder="Enter model name..."
                            className="mt-1"
                          />
                        )}
                      </div>
                    </div>

                    {/* Temperature & API Key */}
                    <div className="space-y-4">
                      <div className="space-y-2">
                        <Label className="text-sm font-medium">
                          Temperature: {settings.temperature || 0.7}
                        </Label>
                        <input
                          type="range"
                          min="0"
                          max="1"
                          step="0.1"
                          value={settings.temperature || 0.7}
                          onChange={(e) =>
                            setSettings({
                              ...settings,
                              temperature: parseFloat(e.target.value),
                            })
                          }
                          className="w-full"
                        />
                      </div>
                      {settings.aiProvider && (
                        <div className="space-y-2">
                          <Label className="text-sm font-medium flex items-center gap-2">
                            <Key className="h-4 w-4" />
                            {settings.aiProvider} API Key
                          </Label>
                          <Input
                            type="password"
                            placeholder={apiKeys[settings.aiProvider] ? "••••••••" : "Enter API key..."}
                            value={apiKeys[settings.aiProvider] || ""}
                            onChange={(e) => {
                              const provider = settings.aiProvider
                              if (provider) {
                                const newKeys = { ...apiKeys, [provider]: e.target.value }
                                setApiKeys(newKeys)
                                localStorage.setItem("sim-creator-api-keys", JSON.stringify(newKeys))
                              }
                            }}
                          />
                        </div>
                      )}
                      </div>
                      </div>
                    </CardContent>
                  </Card>
                )}
              </div>
            </div>
          </div>

            {/* Main Content */}
            <div className="space-y-6">
              {(isGenerating || generationCompleted || generationFailed) && (
            <Card>
              <CardContent className="pt-6">
                <div className="space-y-3">
                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-3">
                      {isGenerating && (
                        <div className="flex items-center gap-2">
                          <div className="animate-spin h-5 w-5 border-2 border-blue-500 border-t-transparent rounded-full"></div>
                          <div className="flex gap-1">
                            <div className="w-2 h-2 bg-blue-500 rounded-full animate-bounce" style={{animationDelay: '0ms'}}></div>
                            <div className="w-2 h-2 bg-blue-500 rounded-full animate-bounce" style={{animationDelay: '150ms'}}></div>
                            <div className="w-2 h-2 bg-blue-500 rounded-full animate-bounce" style={{animationDelay: '300ms'}}></div>
                          </div>
                        </div>
                      )}
                      {generationCompleted && (
                        <CheckCircle className="h-5 w-5 text-green-500" />
                      )}
                      {generationFailed && (
                        <AlertCircle className="h-5 w-5 text-red-500" />
                      )}
                      <span className="text-sm font-medium">
                        {isGenerating && "AI is thinking..."}
                        {generationCompleted && "Generation completed!"}
                        {generationFailed && "Generation failed"}
                      </span>
                    </div>
                  </div>

                  {generationStatus?.error && (
                    <div className="text-sm text-red-600 mt-2">
                      Error: {generationStatus.error}
                    </div>
                  )}

                  {generationStatus?.metadata && (
                    <div className="text-xs text-muted-foreground grid grid-cols-2 gap-2 mt-2">
                      <span>Model: {generationStatus.metadata.aiModel}</span>
                      <span>
                        Tokens: {generationStatus.metadata.tokensUsed}
                      </span>
                      <span>
                        Cost: $
                        {generationStatus.metadata.estimatedCost.toFixed(4)}
                      </span>
                      <span>
                        Time:{" "}
                        {(
                          generationStatus.metadata.processingTimeMs / 1000
                        ).toFixed(1)}
                        s
                      </span>
                    </div>
                  )}
                </div>
              </CardContent>
            </Card>
          )}

          <Tabs value={currentStep} onValueChange={(value) => {
            const step = value as GenerationStep
            if (isStepAccessible(step)) {
              setCurrentStep(step)
              setUserSelectedStep(value)
            }
          }} className="mt-6">
            <TabsList className="grid w-full grid-cols-8">
              <TabsTrigger
                value="scenario"
                disabled={!isStepAccessible("scenario")}
                className={!isStepAccessible("scenario") ? "opacity-50 cursor-not-allowed" : ""}
              >
                <span className="flex items-center gap-2">
                  Basic Info
                  {scenario.industry && scenario.companyDescription ? "✅" : ""}
                </span>
              </TabsTrigger>
              <TabsTrigger
                value="scenario-details"
                disabled={!isStepAccessible("scenario-details")}
                className={!isStepAccessible("scenario-details") ? "opacity-50 cursor-not-allowed" : ""}
              >
                <span className="flex items-center gap-2">
                  Scenario
                  {stepData.scenarioDetails ? "✅" : ""}
                </span>
              </TabsTrigger>
              <TabsTrigger
                value="stakeholders"
                disabled={!isStepAccessible("stakeholders")}
                className={!isStepAccessible("stakeholders") ? "opacity-50 cursor-not-allowed" : ""}
              >
                <span className="flex items-center gap-2">
                  Stakeholders
                  {stepData.stakeholders ? "✅" : ""}
                </span>
              </TabsTrigger>
              <TabsTrigger
                value="messages"
                disabled={!isStepAccessible("messages")}
                className={!isStepAccessible("messages") ? "opacity-50 cursor-not-allowed" : ""}
              >
                <span className="flex items-center gap-2">
                  Messages
                  {stepData.messages ? "✅" : ""}
                </span>
              </TabsTrigger>
              <TabsTrigger
                value="actions"
                disabled={!isStepAccessible("actions")}
                className={!isStepAccessible("actions") ? "opacity-50 cursor-not-allowed" : ""}
              >
                <span className="flex items-center gap-2">
                  Actions
                  {stepData.actions ? "✅" : ""}
                </span>
              </TabsTrigger>
              <TabsTrigger
                value="information-search"
                disabled={!isStepAccessible("information-search")}
                className={!isStepAccessible("information-search") ? "opacity-50 cursor-not-allowed" : ""}
              >
                <span className="flex items-center gap-2">
                  Info Search
                  {stepData.informationSearch ? "✅" : ""}
                </span>
              </TabsTrigger>
              <TabsTrigger
                value="measure-mapping"
                disabled={!isStepAccessible("measure-mapping")}
                className={!isStepAccessible("measure-mapping") ? "opacity-50 cursor-not-allowed" : ""}
              >
                <span className="flex items-center gap-2">
                  Measures
                </span>
              </TabsTrigger>
              <TabsTrigger
                value="complete"
                disabled={!isStepAccessible("complete")}
                className={!isStepAccessible("complete") ? "opacity-50 cursor-not-allowed" : ""}
              >
                <span className="flex items-center gap-2">
                  Complete
                  {stepData.actions ? "🎉" : ""}
                </span>
              </TabsTrigger>
            </TabsList>
            {renderCurrentStep()}
          </Tabs>
            </div>
          </div>
        </div>
      </div>
    </div>
  )
}

export default SimulationCreator
